import { ActionBlock } from '@/components/product/ActionBlock';
import { FavoriteButton } from '@/components/product/FavoriteButton';
import { PriceBlock } from '@/components/product/PriceBlock';
import { ProductPropsBadge } from '@/components/product/ProductPropsBadge';
import { useLanguage } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { ProductListItem } from '@/types';
import '@oddbird/popover-polyfill';
import { route } from 'ziggy-js';

interface ProductCardProps {
    isAuthorized: boolean;
    variation: ProductListItem;
}

export function ProductCard({ isAuthorized, variation }: ProductCardProps) {
    const { t } = useLanguage();

    //Badges
    const isNewBadgeVisible: boolean = variation.isNew && variation.inStock;

    const isAvailable = variation.inStock;

    const tareBadge = variation.packaging_type ?? null;
    const volumeBadge = variation.volume ? `${variation.volume}л` : null;

    const saveItemId = (id: string) => {
        sessionStorage.setItem('last_item_id', id);
    };

    const detailsArray = variation.details
        ? variation?.details.split('|').map((item) => item.trim())
        : [];

    return (
        <article
            className={cn(
                'group relative flex h-full flex-col overflow-hidden rounded-md border border-zinc-800 bg-zinc-900/20 transition-all duration-500',
                isAvailable
                    ? 'hover:translate-y-[-4px] hover:border-brand-gold/30 hover:shadow-2xl hover:shadow-brand-gold/5'
                    : 'border-zinc-900 bg-zinc-950/40 opacity-70 shadow-none',
            )}
            id={variation.sku_code}
            data-id={variation.id}
        >
            {/* Badges Container */}
            <div className="pointer-events-none absolute top-3 left-3 z-10 flex flex-col items-start gap-1.5">
                {isNewBadgeVisible && (
                    <span className="inline-flex items-center rounded-md border border-cyan-600/50 bg-cyan-600 px-2.5 py-1 text-sm font-black tracking-wider whitespace-nowrap text-white uppercase md:text-[10px]">
                        {t('common.new')}
                    </span>
                )}
            </div>

            {/* Favorite Button */}
            {isAuthorized && (
                <FavoriteButton
                    productVariation={variation}
                    className="absolute top-3 right-3 z-20"
                />
            )}

            {/* Image Area */}
            <a
                href={route('product', { slug: variation.slug })}
                onClick={() => saveItemId(variation.sku_code)}
                className="relative isolate aspect-square w-full shrink-0 cursor-pointer overflow-hidden bg-zinc-950"
                style={{
                    transform: 'translateZ(0)',
                    maskImage: 'linear-gradient(to bottom, black, black)', // Хак для Webkit: принудительно сглаживает края при overflow:hidden
                }}
            >
                <img
                    src={variation.label}
                    alt={variation.title}
                    className={cn(
                        '-m-[1px] h-[calc(100%+2px)] w-[calc(100%+2px)] object-cover opacity-90 transition-transform duration-700 ease-[cubic-bezier(0.4,0,0.2,1)]',
                        'transform-gpu will-change-transform',
                        isAvailable &&
                            'group-hover:scale-110 group-hover:opacity-100',
                        !isAvailable && 'brightness-[0.4] grayscale',
                    )}
                    style={{
                        backfaceVisibility: 'hidden',
                        perspective: '1000px',
                        WebkitBackfaceVisibility: 'hidden',
                    }}
                    loading="lazy"
                />

                {!isAvailable && (
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                        <span className="rounded border border-red-500/40 bg-red-500/10 px-3 py-1.5 text-[11px] font-black tracking-[0.25em] text-red-500 uppercase shadow-2xl backdrop-blur-sm">
                            {t('common.outOfStock')}
                        </span>
                    </div>
                )}
            </a>

            {/* Content */}
            <div className="flex flex-1 flex-col p-4">
                <header className="mb-2">
                    <p className="mb-1.5 text-sm font-bold tracking-[0.15em] text-brand-gold uppercase opacity-80 md:text-xs">
                        {variation.vendor}
                    </p>
                    <a
                        href={route('product', { slug: variation.slug })}
                        onClick={() => saveItemId(variation.sku_code)}
                    >
                        <h1
                            className={cn(
                                "line-clamp-2 cursor-pointer font-['Oswald'] text-xl leading-tight font-bold tracking-wide text-zinc-100 uppercase transition-colors md:text-lg",
                                isAvailable && 'hover:text-brand-gold',
                            )}
                        >
                            {variation.name}
                        </h1>
                    </a>
                    <p className="mt-2 text-sm font-medium text-zinc-500 md:text-xs">
                        {variation.style}
                    </p>
                </header>

                <div className="mt-auto flex flex-col gap-3 border-t border-zinc-800/50 pt-2">
                    <div className="flex flex-col">
                        <div className="align-center flex flex-wrap gap-1">
                            {variation.untappd && (
                                <ProductPropsBadge
                                    value={variation.untappd.score}
                                    untappd={true}
                                />
                            )}
                            {detailsArray.length > 0 &&
                                detailsArray?.map((item) => (
                                    <ProductPropsBadge
                                        key={item}
                                        value={item}
                                    />
                                ))}
                        </div>

                        <div className="mt-1">
                            {tareBadge && (
                                <ProductPropsBadge
                                    value={tareBadge}
                                    className="mr-1"
                                    bgColorClass="border-zinc-700/50 bg-zinc-800/80"
                                />
                            )}
                            {volumeBadge && (
                                <ProductPropsBadge
                                    value={volumeBadge}
                                    bgColorClass="border-zinc-700/50 bg-zinc-800/80"
                                />
                            )}
                        </div>
                    </div>
                    <PriceBlock variation={variation} />
                    {isAuthorized && <ActionBlock variation={variation} />}
                </div>
            </div>
        </article>
    );
}
